import mongoose from "mongoose";

import {
  Attendance,
  ATTENDANCE_LOG_TYPE,
  ATTENDANCE_STATUS,
  ATTENDANCE_STATUS_CHECK_IN,
  AttendanceAudit,
  AttendanceCourseSummary,
  AttendanceLog,
  BreakTimer,
  Class,
  NOTIFICATION_TYPE,
  Student,
} from "../models";
import { logger } from "../utils/logger";
import { notificationService } from "./notification-service";

const DAY_NAMES = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

const ATTENDANCE_FLAG_THRESHOLD = 20;

const dateKeyUTC = (d: Date): string => {
  const x = new Date(d);
  return `${x.getUTCFullYear()}-${String(x.getUTCMonth() + 1).padStart(2, "0")}-${String(x.getUTCDate()).padStart(2, "0")}`;
};

export function getValidClassDates(
  classItem: {
    schedule?: { day: string; isActive?: boolean }[];
    createdAt?: Date;
  },
  joinDate: Date,
  until: Date = new Date(),
): string[] {
  const schedule = classItem.schedule ?? [];
  const activeScheduleDays = new Set(
    schedule.filter(s => s.isActive !== false).map(s => s.day.toLowerCase()),
  );
  if (activeScheduleDays.size === 0) {
    return [];
  }

  const todayUTC = new Date(
    Date.UTC(until.getUTCFullYear(), until.getUTCMonth(), until.getUTCDate()),
  );
  const joinUTCStart = new Date(
    Date.UTC(
      joinDate.getUTCFullYear(),
      joinDate.getUTCMonth(),
      joinDate.getUTCDate(),
    ),
  );
  const validDates: string[] = [];
  const cursor = new Date(joinUTCStart.getTime());
  while (cursor <= todayUTC) {
    const dayName = DAY_NAMES[cursor.getUTCDay()];
    if (activeScheduleDays.has(dayName)) {
      validDates.push(dateKeyUTC(cursor));
    }
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return validDates;
}

export async function computeAttendanceSummary(
  classId: mongoose.Types.ObjectId,
  studentId: mongoose.Types.ObjectId,
  classItem: {
    schedule?: { day: string; isActive?: boolean }[];
    createdAt?: Date;
    schoolId?: mongoose.Types.ObjectId;
  },
  joinDate: Date,
): Promise<{
  totalClassesHeld: number;
  classesPresent: number;
  classesLate: number;
  classesAbsent: number;
  attendancePercentage: number;
}> {
  const validDates = getValidClassDates(classItem, joinDate);
  const totalClassesHeld = validDates.length;

  if (totalClassesHeld === 0) {
    return {
      totalClassesHeld: 0,
      classesPresent: 0,
      classesLate: 0,
      classesAbsent: 0,
      attendancePercentage: 0,
    };
  }

  const student = await Student.findById(studentId).select("userId").lean();
  if (!student?.userId) {
    return {
      totalClassesHeld,
      classesPresent: 0,
      classesLate: 0,
      classesAbsent: totalClassesHeld,
      attendancePercentage: 0,
    };
  }

  const joinUTCStart = new Date(
    Date.UTC(
      joinDate.getUTCFullYear(),
      joinDate.getUTCMonth(),
      joinDate.getUTCDate(),
    ),
  );
  const now = new Date();
  const todayEndUTC = new Date(
    Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
      23,
      59,
      59,
      999,
    ),
  );

  const [logs, manualAttendance] = await Promise.all([
    AttendanceLog.find({
      userId: student.userId,
      classId,
      userRole: "student",
      logType: ATTENDANCE_LOG_TYPE.CHECK_IN,
      timestamp: { $gte: joinUTCStart, $lte: todayEndUTC },
    }).lean(),
    Attendance.find({
      classId,
      studentId,
      date: { $gte: joinUTCStart, $lte: todayEndUTC },
    }).lean(),
  ]);

  const attendedDates = new Map<string, "present" | "late">();

  for (const log of logs) {
    const key = dateKeyUTC(log.timestamp);
    if (!validDates.includes(key)) continue;
    const status =
      log.attendanceStatus === ATTENDANCE_STATUS_CHECK_IN.LATE
        ? "late"
        : "present";
    attendedDates.set(key, status);
  }

  for (const att of manualAttendance) {
    const key = dateKeyUTC(att.date);
    if (!validDates.includes(key)) continue;
    if (att.status === ATTENDANCE_STATUS.LATE) {
      attendedDates.set(key, "late");
    } else if (
      att.status === ATTENDANCE_STATUS.PRESENT ||
      att.status === ATTENDANCE_STATUS.IN_CLASS ||
      att.status === ATTENDANCE_STATUS.LATE_ATTENDANCE
    ) {
      attendedDates.set(
        key,
        attendedDates.get(key) === "late" ? "late" : "present",
      );
    }
  }

  let classesPresent = 0;
  let classesLate = 0;
  for (const status of attendedDates.values()) {
    if (status === "late") classesLate += 1;
    else classesPresent += 1;
  }

  const classesAttended = attendedDates.size;
  const classesAbsent = Math.max(0, totalClassesHeld - classesAttended);
  const attendancePercentage =
    totalClassesHeld > 0
      ? Number(
          (((classesPresent + classesLate) / totalClassesHeld) * 100).toFixed(
            2,
          ),
        )
      : 0;

  return {
    totalClassesHeld,
    classesPresent,
    classesLate,
    classesAbsent,
    attendancePercentage,
  };
}

export async function refreshAttendanceSummary(
  classId: mongoose.Types.ObjectId,
  studentId: mongoose.Types.ObjectId,
  joinDate: Date,
): Promise<typeof AttendanceCourseSummary.prototype> {
  const classItem = await Class.findById(classId).lean();
  if (!classItem) {
    throw new Error("Class not found");
  }

  const stats = await computeAttendanceSummary(
    classId,
    studentId,
    classItem,
    joinDate,
  );

  const attendanceFlagged =
    stats.attendancePercentage < ATTENDANCE_FLAG_THRESHOLD;
  const existing = await AttendanceCourseSummary.findOne({
    classId,
    studentId,
  });
  const wasFlagged = existing?.attendanceFlagged ?? false;

  const summary = await AttendanceCourseSummary.findOneAndUpdate(
    { classId, studentId },
    {
      schoolId: classItem.schoolId,
      ...stats,
      attendanceFlagged,
      ...(attendanceFlagged && !wasFlagged
        ? { flaggedAt: new Date(), flagNotifiedAt: null }
        : {}),
      ...(attendanceFlagged ? {} : { flaggedAt: null, flagNotifiedAt: null }),
    },
    { upsert: true, new: true, setDefaultsOnInsert: true },
  );

  if (attendanceFlagged && !wasFlagged && !summary.flagNotifiedAt) {
    try {
      const student = await Student.findById(studentId);
      if (student) {
        await notificationService.createNotification(
          student.userId,
          NOTIFICATION_TYPE.ATTENDANCE_FLAG,
          "Attendance Below 20%",
          `Your attendance in this course has dropped below 20%. Please contact your school.`,
          {
            metadata: {
              classId: classId.toString(),
              studentId: studentId.toString(),
              attendancePercentage: stats.attendancePercentage,
            },
            schoolId: classItem.schoolId,
          },
        );
        summary.flagNotifiedAt = new Date();
        await summary.save();
      }
    } catch (error) {
      logger.error("Failed to send attendance flag notification:", error);
    }
  }

  return summary;
}

export function resolveEditableState(
  hasCheckIn: boolean,
  semesterLocked = false,
): {
  isEditable: boolean;
  editableReason: "no_checkin" | "locked_checkin" | "semester_locked";
} {
  if (semesterLocked) {
    return { isEditable: false, editableReason: "semester_locked" };
  }
  if (hasCheckIn) {
    return { isEditable: false, editableReason: "locked_checkin" };
  }
  return { isEditable: true, editableReason: "no_checkin" };
}

export async function recordAttendanceAudit(params: {
  schoolId: mongoose.Types.ObjectId;
  classId: mongoose.Types.ObjectId;
  studentId: mongoose.Types.ObjectId;
  date: Date;
  changedBy: mongoose.Types.ObjectId;
  changedByRole: string;
  fromStatus?: string;
  toStatus: string;
  reason?: string;
}): Promise<void> {
  await AttendanceAudit.create(params);
}

export async function getBreaksReportForClass(
  classId: mongoose.Types.ObjectId,
): Promise<
  {
    studentId: string;
    studentName: string;
    profilePicture: { fileUrl?: string } | null;
    totalBreaks: number;
    totalBreakMinutes: number;
    breaks: {
      date: string;
      breakIn: string;
      breakOut: string;
      durationMinutes: number;
    }[];
  }[]
> {
  const classItem = await Class.findById(classId).select("studentIds").lean();
  if (!classItem?.studentIds?.length) {
    return [];
  }

  const students = await Student.find({
    _id: { $in: classItem.studentIds },
  })
    .select("fullName profilePicture userId")
    .lean();

  const records = await Promise.all(
    students.map(async student => {
      const breaks = await BreakTimer.find({
        userId: student.userId,
        classId,
        userRole: "student",
        status: "completed",
        endedAt: { $exists: true, $ne: null },
      })
        .sort({ startedAt: 1 })
        .lean();

      const mappedBreaks = breaks
        .filter((b): b is typeof b & { startedAt: Date; endedAt: Date } =>
          Boolean(b.startedAt && b.endedAt),
        )
        .map(b => {
          const durationMinutes = Math.round(
            (new Date(b.endedAt).getTime() - new Date(b.startedAt).getTime()) /
              60_000,
          );
          return {
            date: dateKeyUTC(b.startedAt),
            breakIn: new Date(b.startedAt).toISOString(),
            breakOut: new Date(b.endedAt).toISOString(),
            durationMinutes,
          };
        });

      const totalBreakMinutes = mappedBreaks.reduce(
        (sum, b) => sum + b.durationMinutes,
        0,
      );

      return {
        studentId: student._id.toString(),
        studentName: student.fullName,
        profilePicture: student.profilePicture
          ? {
              fileUrl: (student.profilePicture as { fileUrl?: string }).fileUrl,
            }
          : null,
        totalBreaks: mappedBreaks.length,
        totalBreakMinutes,
        breaks: mappedBreaks,
      };
    }),
  );

  return records;
}

export function formatSummaryResponse(
  classId: mongoose.Types.ObjectId | string,
  studentId: mongoose.Types.ObjectId | string,
  summary: {
    totalClassesHeld: number;
    classesPresent: number;
    classesLate: number;
    classesAbsent: number;
    attendancePercentage: number;
    attendanceFlagged: boolean;
    flaggedAt?: Date;
    flagNotifiedAt?: Date;
  },
): Record<string, unknown> {
  return {
    classId: classId.toString(),
    studentId: studentId.toString(),
    totalClassesHeld: summary.totalClassesHeld,
    classesAttended: summary.classesPresent + summary.classesLate,
    classesPresent: summary.classesPresent,
    classesLate: summary.classesLate,
    classesAbsent: summary.classesAbsent,
    attendancePercentage: summary.attendancePercentage,
    attendanceFlagged: summary.attendanceFlagged,
    flaggedAt: summary.flaggedAt ?? null,
    flagNotifiedAt: summary.flagNotifiedAt ?? null,
  };
}
